import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import * as Msal from 'msal';

import { ActionText } from '@components/action-text';
import { createForm } from '@components/form';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useLoginAzure } from 'api/authentication/hooks';
import { useAzureAdSettingsForEmail } from 'api/company/hooks';

import { Title } from './common';

import { routePaths } from '../navigation';

const Form = createForm<{ email: string }>();

export const AzureADPage = () => {
  const { t } = useScopedTranslate(locale);

  const location = useLocation();
  const locationState = location.state;
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { refetch } = useAzureAdSettingsForEmail(email);
  const signIn = useLoginAzure();
  const client = useQueryClient();

  const submit = async (values: { email: string }) => {
    const email = values.email;
    const { data, isError } = await refetch();
    if (isError) {
      setErrors({ email: t('accountNotConfigured') });
      return;
    }
    if (!data) return;
    const { clientId, tenantId } = data;
    const msalConfig = {
      auth: {
        clientId: clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin,
      },
    };
    const msalInstance = new Msal.UserAgentApplication(msalConfig);
    const loginRequest = {
      scopes: ['user.read'],
    };

    try {
      const response = await msalInstance.loginPopup(loginRequest);
      await signIn.mutateAsync({ token: response.idToken.rawIdToken, email });
      client.resetQueries();
      const redir = locationState && (locationState.from ? locationState.from : '/');
      navigate(redir);
    } catch (error) {
      setErrors({ email: t('accountNotConfigured') });
    }
  };
  return (
    <Stack space={6}>
      <Title>{t('signinWithMicrosoft')}</Title>
      <Form onSubmit={submit} updateErrors={errors}>
        <Form.TextField
          label={t('email')}
          id="email"
          validators={['required', 'email']}
          onChange={setEmail}
        />
        <Form.Submit text={t('signin')} />
      </Form>
      <ActionText.Link
        to={`/${routePaths.signin}`}
        children={t('signinWithEmail')}
        font="regular14"
        color="textHelper"
      />
    </Stack>
  );
};

const locale = {
  en: {
    signinWithMicrosoft: 'Sign in with Microsoft',
    email: 'Email',
    signin: 'Sign in',
    signinWithEmail: 'Sign in with email and password',
    accountNotConfigured:
      'Unable to sign-in, please make sure the company allows this sign-in method and that your user is eligible',
  },
};
